import React, { useState, useEffect } from 'react';
import { FileText, Folder, FolderOpen, Search, Loader, AlertCircle, Download, ChevronDown, ChevronRight, X, Eye } from 'lucide-react';
import { documenti, fascicoli, downloadDocumento, anteprimaDocumento } from '../api';

export default function Documenti() {
  const [listaFascicoli, setListaFascicoli] = useState([]);
  const [carico, setCarico] = useState(true);
  const [errore, setErrore] = useState(null);
  const [filtro, setFiltro] = useState('');
  const [aperti, setAperti] = useState({});
  const [docsPerFascicolo, setDocsPerFascicolo] = useState({});
  const [caricoFascicolo, setCaricoFascicolo] = useState({});

  // Anteprima
  const [docSelezionato, setDocSelezionato] = useState(null);
  const [anteprimaUrl, setAnteprimaUrl] = useState(null);
  const [anteprimaHtml, setAnteprimaHtml] = useState(null);
  const [anteprimaTipo, setAnteprimaTipo] = useState(null);
  const [caricoAnteprima, setCaricoAnteprima] = useState(false);

  const [mostraVuoti, setMostraVuoti] = useState(false);
  const carica = async (vuoti = mostraVuoti) => {
    setCarico(true);
    setErrore(null);
    try {
      const params = {};
      if (filtro) params.cliente = filtro;
      if (vuoti) params.includi_vuoti = true;
      const r = await fascicoli(params);
      setListaFascicoli(r.data);
      setAperti({});
      setDocsPerFascicolo({});
    } catch {
      setErrore('Impossibile caricare i fascicoli.');
    } finally {
      setCarico(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { carica(); }, []);

  const toggleFascicolo = async (fasc) => {
    const isAperto = aperti[fasc];
    setAperti({ ...aperti, [fasc]: !isAperto });
    if (!isAperto && !docsPerFascicolo[fasc]) {
      setCaricoFascicolo(prev => ({ ...prev, [fasc]: true }));
      try {
        const params = fasc === '(senza fascicolo)' ? { fascicolo: '__SENZA__' } : { fascicolo: fasc };
        const r = await documenti(params);
        setDocsPerFascicolo(prev => ({ ...prev, [fasc]: r.data }));
      } catch {
        setDocsPerFascicolo(prev => ({ ...prev, [fasc]: [] }));
      } finally {
        setCaricoFascicolo(prev => ({ ...prev, [fasc]: false }));
      }
    }
  };

  const apriAnteprima = async (doc) => {
    setDocSelezionato(doc);
    setCaricoAnteprima(true);
    setAnteprimaUrl(null);
    setAnteprimaHtml(null);
    // pulisci url precedente
    if (anteprimaUrl) window.URL.revokeObjectURL(anteprimaUrl);
    const tipo = (doc.tipo || '').toLowerCase();
    setAnteprimaTipo(tipo);
    try {
      const res = await anteprimaDocumento(doc.id);
      if (tipo === 'pdf' || ['png','jpg','jpeg','gif','webp'].includes(tipo)) {
        const url = window.URL.createObjectURL(res.data);
        setAnteprimaUrl(url);
      } else {
        // HTML (docx, eml, txt) — il blob contiene HTML
        const testo = await res.data.text();
        setAnteprimaHtml(testo);
      }
    } catch (err) {
      setAnteprimaHtml('<div style="padding:20px;color:#e53e3e;">Errore nel caricamento dell anteprima</div>');
    } finally {
      setCaricoAnteprima(false);
    }
  };

  const chiudiAnteprima = () => {
    if (anteprimaUrl) window.URL.revokeObjectURL(anteprimaUrl);
    setDocSelezionato(null);
    setAnteprimaUrl(null);
    setAnteprimaHtml(null);
    setAnteprimaTipo(null);
  };

  const tipoColore = {
    pdf: '#e53e3e', docx: '#3182ce', doc: '#3182ce',
    txt: '#718096', eml: '#d69e2e', xml: '#38a169'
  };

  const sottocartella = (percorso, fascicolo) => {
    if (!percorso || !fascicolo) return null;
    const marker = `/${fascicolo}/`;
    const idx = percorso.indexOf(marker);
    if (idx === -1) return null;
    const dopo = percorso.substring(idx + marker.length);
    const parti = dopo.split('/');
    return parti.length > 1 ? parti[0] : null;
  };

  const raggruppaPerSottocartella = (docs, fascicolo) => {
    const gruppi = { _root: [] };
    docs.forEach(doc => {
      const sub = sottocartella(doc.percorso, fascicolo);
      if (sub) {
        if (!gruppi[sub]) gruppi[sub] = [];
        gruppi[sub].push(doc);
      } else {
        gruppi._root.push(doc);
      }
    });
    return gruppi;
  };

  const renderDoc = (doc) => (
    <div
      key={doc.id}
      className="doc-card"
      style={{
        marginBottom: '6px',
        cursor: 'pointer',
        outline: docSelezionato?.id === doc.id ? '2px solid #3182ce' : 'none'
      }}
      onClick={() => apriAnteprima(doc)}
    >
      <div className="doc-tipo" style={{ background: tipoColore[doc.tipo] || '#718096' }}>
        {doc.tipo?.toUpperCase()}
      </div>
      <div className="doc-info" style={{ flex: 1 }}>
        <div className="doc-nome">{doc.nome_file}</div>
        <div className="doc-meta">
          <span>{doc.num_chunks} chunk</span>
          <span> · {new Date(doc.data_ingestion).toLocaleDateString('it-IT')}</span>
        </div>
      </div>
      <button
        className="btn-download"
        onClick={(e) => { e.stopPropagation(); downloadDocumento(doc.id, doc.nome_file); }}
        title="Scarica documento originale"
      >
        <Download size={14} /> Scarica
      </button>
    </div>
  );

  return (
    <div className="pagina pagina-documenti" style={{ display: 'flex', gap: '16px' }}>
      {/* Colonna sinistra: lista fascicoli */}
      <div style={{ flex: docSelezionato ? '0 0 45%' : '1', transition: 'flex 0.2s', minWidth: 0 }}>
        <div className="filtri-bar">
          <input
            type="text"
            className="input-filtro"
            placeholder="Cerca per cliente o fascicolo..."
            value={filtro}
            onChange={e => setFiltro(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && carica()}
          />
          <button className="btn-cerca" onClick={carica}>
            <Search size={16} /> Filtra
          </button>
        </div>

        {errore && (
          <div className="alert errore">
            <AlertCircle size={18} /><span>{errore}</span>
          </div>
        )}

        {carico ? (
          <div className="carico-box">
            <Loader size={32} className="spin" />
            <p>Caricamento fascicoli...</p>
          </div>
        ) : (
          <>
            <div style={{ fontSize: 13, color: '#718096', marginBottom: 10 }}>
              {listaFascicoli.length} fascicol{listaFascicoli.length === 1 ? 'o' : 'i'}{mostraVuoti ? '' : ' con documenti'}{' '}
              <button onClick={() => { const v = !mostraVuoti; setMostraVuoti(v); carica(v); }}
                style={{ background: 'none', border: 'none', color: '#2E75B6', cursor: 'pointer',
                  fontFamily: 'inherit', fontSize: 13, padding: 0, textDecoration: 'underline' }}>
                {mostraVuoti ? 'Nascondi fascicoli vuoti' : 'Mostra anche fascicoli vuoti'}
              </button>
            </div>
            <div className="doc-lista">
              {listaFascicoli.length === 0 ? (
                <div className="vuoto">
                  <FileText size={48} />
                  <p>Nessun fascicolo trovato</p>
                </div>
              ) : listaFascicoli.map(f => {
                const docs = docsPerFascicolo[f.fascicolo] || [];
                const gruppi = raggruppaPerSottocartella(docs, f.fascicolo);
                const sottocartelle = Object.keys(gruppi).filter(k => k !== '_root');
                return (
                  <div key={f.fascicolo} className="fascicolo-blocco">
                    <div
                      className="fascicolo-header"
                      onClick={() => toggleFascicolo(f.fascicolo)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: '10px',
                        cursor: 'pointer', padding: '14px 16px', background: '#fff',
                        border: '1px solid #e2e8f0', borderRadius: aperti[f.fascicolo] ? '8px 8px 0 0' : '8px',
                        marginBottom: aperti[f.fascicolo] ? 0 : '8px'
                      }}
                    >
                      {aperti[f.fascicolo] ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                      {aperti[f.fascicolo] ? <FolderOpen size={20} color="#d69e2e" /> : <Folder size={20} color="#d69e2e" />}
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 600 }}>{f.fascicolo}</div>
                        {f.descrizione && (
                          <div style={{ fontSize: '0.9em', color: '#2d3748', marginTop: '2px' }}>{f.descrizione}</div>
                        )}
                        <div style={{ fontSize: '0.85em', color: '#718096', marginTop: '2px' }}>
                          {f.cliente && <span>Cliente: {f.cliente} · </span>}
                          {f.num_documenti} document{f.num_documenti === 1 ? 'o' : 'i'}
                        </div>
                      </div>
                    </div>

                    {aperti[f.fascicolo] && (
                      <div style={{
                        border: '1px solid #e2e8f0', borderTop: 'none',
                        borderRadius: '0 0 8px 8px', marginBottom: '8px',
                        background: '#f8fafc', padding: '8px'
                      }}>
                        {caricoFascicolo[f.fascicolo] ? (
                          <div style={{ padding: '12px', textAlign: 'center' }}>
                            <Loader size={20} className="spin" />
                          </div>
                        ) : (
                          <>
                            {gruppi._root.map(renderDoc)}
                            {sottocartelle.map(sub => (
                              <div key={sub} style={{ marginTop: '8px' }}>
                                <div style={{
                                  display: 'flex', alignItems: 'center', gap: '8px',
                                  padding: '8px 10px', background: '#edf2f7',
                                  borderRadius: '6px', marginBottom: '6px', fontWeight: 500
                                }}>
                                  <Folder size={16} color="#d69e2e" />
                                  {sub}
                                  <span style={{ fontSize: '0.85em', color: '#718096', fontWeight: 400 }}>
                                    ({gruppi[sub].length})
                                  </span>
                                </div>
                                <div style={{ paddingLeft: '16px' }}>
                                  {gruppi[sub].map(renderDoc)}
                                </div>
                              </div>
                            ))}
                          </>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>

      {/* Colonna destra: pannello anteprima */}
      {docSelezionato && (
        <div style={{
          flex: '1', minWidth: 0, background: '#fff',
          border: '1px solid #e2e8f0', borderRadius: '8px',
          display: 'flex', flexDirection: 'column',
          height: 'calc(100vh - 120px)', position: 'sticky', top: '16px'
        }}>
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '12px 16px', borderBottom: '1px solid #e2e8f0'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', overflow: 'hidden' }}>
              <Eye size={18} color="#3182ce" />
              <span style={{ fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {docSelezionato.nome_file}
              </span>
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                className="btn-download"
                onClick={() => downloadDocumento(docSelezionato.id, docSelezionato.nome_file)}
                title="Scarica"
              >
                <Download size={14} /> Scarica
              </button>
              <button
                onClick={chiudiAnteprima}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px' }}
                title="Chiudi anteprima"
              >
                <X size={20} />
              </button>
            </div>
          </div>

          <div style={{ flex: 1, overflow: 'auto', background: '#f8fafc' }}>
            {caricoAnteprima ? (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                <Loader size={32} className="spin" />
              </div>
            ) : anteprimaTipo === 'pdf' && anteprimaUrl ? (
              <iframe src={anteprimaUrl} title="Anteprima PDF" style={{ width: '100%', height: '100%', border: 'none' }} />
            ) : ['png','jpg','jpeg','gif','webp'].includes(anteprimaTipo) && anteprimaUrl ? (
              <div style={{ padding: '16px', textAlign: 'center' }}>
                <img src={anteprimaUrl} alt={docSelezionato.nome_file} style={{ maxWidth: '100%', height: 'auto' }} />
              </div>
            ) : anteprimaHtml ? (
              <div style={{ background: '#fff', margin: '12px', borderRadius: '6px' }}
                   dangerouslySetInnerHTML={{ __html: anteprimaHtml }} />
            ) : (
              <div style={{ padding: '20px', color: '#999', textAlign: 'center' }}>
                Anteprima non disponibile
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
